import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import AnimatedSection from '../../components/AnimatedSection';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from '../../components/Icons';
import { db } from '../../firebase'; 
import { doc, getDoc, collection, query, orderBy, getDocs } from 'firebase/firestore';

const ProjectImageDetailPage = () => {
  const { imageId } = useParams();
  const location = useLocation(); 
  const navigate = useNavigate();
  const imageDetailRef = useRef(null); 
  
  const [currentPhoto, setCurrentPhoto] = useState(location.state?.photoItem || null); 
  const [allGalleryPhotosData, setAllGalleryPhotosData] = useState(location.state?.allPhotos || []); 
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [loading, setLoading] = useState(!currentPhoto); 
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPhotoDetails = async () => {
      if (!imageId) {
        setError("사진 ID가 유효하지 않습니다.");
        setLoading(false);
        return;
      }
      if (!currentPhoto || currentPhoto.id !== imageId) {
        setLoading(true);
        setError(null);
        try {
          const photoDocRef = doc(db, "constructionImages", imageId);
          const docSnap = await getDoc(photoDocRef);

          if (docSnap.exists()) {
            setCurrentPhoto({ id: docSnap.id, ...docSnap.data() });
          } else {
            console.warn(`Photo with ID ${imageId} not found.`);
            setError("해당 사진을 찾을 수 없습니다.");
            setCurrentPhoto(null); 
          }
        } catch (err) {
          console.error("Error fetching photo details: ", err);
          setError("사진 정보를 불러오는 중 오류가 발생했습니다.");
        }
        setLoading(false);
      } else { 
        setLoading(false);
      }
    };

    fetchPhotoDetails();
  }, [imageId, currentPhoto]); 

  useEffect(() => {
    if (currentPhoto && allGalleryPhotosData.length > 0) {
      const idx = allGalleryPhotosData.findIndex(p => p.id === currentPhoto.id);
      setCurrentIndex(idx);
    } else if (currentPhoto && allGalleryPhotosData.length === 0) {
       const fetchAllPhotosForNav = async () => {
          try {
            const photosCollectionRef = collection(db, "constructionImages");
            const q = query(photosCollectionRef, orderBy("uploadedAt", "desc"));
            const querySnapshot = await getDocs(q);
            const photos = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
            setAllGalleryPhotosData(photos);
            const idx = photos.findIndex(p => p.id === currentPhoto.id);
            setCurrentIndex(idx);
          } catch (err) {
            console.error("Error fetching all photos for navigation: ", err);
          }
       }
       fetchAllPhotosForNav();
    }
  }, [currentPhoto, allGalleryPhotosData]);

  useEffect(() => {
    if (currentPhoto && imageDetailRef.current && !location.state?.keepScroll) {
      imageDetailRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [currentPhoto, location.state?.keepScroll]);


  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 text-center min-h-[70vh] flex flex-col justify-center items-center">
        <title>로딩 중... - 진양건설</title>
        <p className="text-xl text-gray-600 dark:text-gray-400">사진 정보를 불러오는 중입니다...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 text-center min-h-[70vh] flex flex-col justify-center items-center">
        <title>오류 - 진양건설</title>
        <p className="text-xl text-red-500 dark:text-red-400">{error}</p>
        <Link to="/projects" className="mt-6 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500">
          <ArrowLeftIcon className="mr-2" /> 갤러리로 돌아가기
        </Link>
      </div>
    );
  }
  
  if (!currentPhoto) { 
    return (
        <div className="container mx-auto px-4 py-16 text-center min-h-[70vh] flex flex-col justify-center items-center">
            <title>사진 정보 없음 - 진양건설</title>
            <p className="text-xl text-gray-600 dark:text-gray-400">요청하신 사진 정보를 찾을 수 없습니다.</p>
            <Link to="/projects" className="mt-6 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500">
                <ArrowLeftIcon className="mr-2" /> 갤러리로 돌아가기
            </Link>
        </div>
    );
  }
  
  const goToPrevImage = () => { 
    if (currentIndex > 0 && allGalleryPhotosData.length > currentIndex -1) { 
      const prevPhoto = allGalleryPhotosData[currentIndex - 1];
      navigate(`/projects/gallery/${prevPhoto.id}`, { 
        state: { 
          photoItem: prevPhoto, 
          allPhotos: allGalleryPhotosData, 
          keepScroll: true 
        } 
      }); 
    }
  };
  const goToNextImage = () => { 
    if (currentIndex < allGalleryPhotosData.length - 1 && allGalleryPhotosData.length > currentIndex + 1) { 
      const nextPhoto = allGalleryPhotosData[currentIndex + 1];
      navigate(`/projects/gallery/${nextPhoto.id}`, { 
        state: { 
          photoItem: nextPhoto, 
          allPhotos: allGalleryPhotosData,
          keepScroll: true 
        } 
      }); 
    }
  };

  return (
    <>
      <title>{currentPhoto.title || "시공 사진 상세"} - 진양건설</title>
      <div ref={imageDetailRef} className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <AnimatedSection animationType="fade-in">
          <div className="mb-8">
            <Link to="/projects" className="inline-flex items-center text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 transition-colors group">
              <ArrowLeftIcon className="mr-2 transition-transform group-hover:-translate-x-1" />
              갤러리로 돌아가기
            </Link>
          </div>

          <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-xl shadow-2xl">
            {/* --- 수정된 제목 부분 --- */}
            <div className="mb-8 text-center">
                <span className="block text-base font-medium text-sky-600 dark:text-sky-400 mb-1">
                    {currentPhoto.category}
                </span>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                    {currentPhoto.title}
                </h1>
                <p className="text-lg text-gray-500 dark:text-gray-400 mt-2">시공 상세 사진</p>
            </div>
            
            <div className="relative mb-6 flex justify-center items-center">
               {currentIndex > 0 && (<button onClick={goToPrevImage} className="absolute left-0 sm:-left-4 md:-left-8 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all opacity-80 hover:opacity-100 focus:outline-none" aria-label="이전 사진"><ChevronLeftIcon className="w-6 h-6 md:w-8 md:h-8"/></button>)}
              
              <img 
                src={currentPhoto.imageUrl} 
                alt={currentPhoto.title} 
                title="" 
                draggable="false" 
                className="max-w-full max-h-[75vh] h-auto rounded-lg shadow-lg object-contain mx-auto select-none" 
                style={{ WebkitUserDrag: 'none' }} 
                onError={(e) => e.target.src = 'https://placehold.co/1200x800/334155/e0f2fe?text=Image+Error'}
                onContextMenu={(e) => e.preventDefault()} 
              />

              {currentIndex < allGalleryPhotosData.length - 1 && (<button onClick={goToNextImage} className="absolute right-0 sm:-right-4 md:-right-8 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all opacity-80 hover:opacity-100 focus:outline-none" aria-label="다음 사진"><ChevronRightIcon className="w-6 h-6 md:w-8 md:h-8"/></button>)}
            </div>
            <div className="text-center md:text-left max-w-3xl mx-auto">
              <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                <span><strong>카테고리:</strong> {currentPhoto.category}</span>
                <span><strong>촬영일:</strong> {currentPhoto.uploadedAt?.toDate ? currentPhoto.uploadedAt.toDate().toLocaleDateString() : (currentPhoto.date || '날짜 정보 없음')}</span>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line text-base md:text-lg">
                {currentPhoto.description || '상세 설명이 없습니다.'}
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </>
  );
};
export default ProjectImageDetailPage;
